import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  ButtonGroup,
  Popover,
  TextField,
  Button,
  Typography,
  Chip,
  Menu
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Link,
  Image,
  FormatAlignLeft,
  FormatListBulleted,
  FormatListNumbered,
  FormatIndentIncrease,
  FormatIndentDecrease,
  Undo,
  Redo,
  FormatClear,
  FontDownload,
  TextFields,
  ViewHeadline,
  TableChart,
  Code,
  CheckBox,
  AutoFixHigh
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';

// Beautiful custom text style selector - replacing ugly MUI Select
const textStyles = [
  { value: 'p', label: 'Normal Text', icon: '📝', preview: 'Regular paragraph text' },
  { value: 'h1', label: 'Heading 1', icon: '#', preview: 'Large section heading', style: { fontSize: '2em', fontWeight: 'bold' } },
  { value: 'h2', label: 'Heading 2', icon: '##', preview: 'Medium section heading', style: { fontSize: '1.5em', fontWeight: 'bold' } },
  { value: 'h3', label: 'Heading 3', icon: '###', preview: 'Small section heading', style: { fontSize: '1.2em', fontWeight: 'bold' } },
  { value: 'blockquote', label: 'Quote', icon: '💬', preview: 'Inspirational quote', style: { fontStyle: 'italic', color: '#666' } },
  { value: 'pre', label: 'Code Block', icon: '💻', preview: 'Code with syntax highlighting', style: { fontFamily: 'monospace', backgroundColor: '#f5f5f5' } }
];

// Sticky toolbar - moves with content but stays visible when scrolling down
const ToolbarContainer = styled(Box)(({ theme, editorTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 16px',
  backgroundColor: editorTheme?.surface || (theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff'),
  borderRadius: '0px', // Removed round corners
  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  border: `1px solid ${editorTheme?.textSecondary || (theme.palette.mode === 'dark' ? '#334155' : '#e2e8f0')}30`,
  flexWrap: 'wrap',
  minHeight: '48px',
  position: 'sticky', // Sticky - moves with content but sticks when scrolling
  top: '20px', // Sticks 20px from top when scrolling
  zIndex: 100,
  marginBottom: '16px', // Space below toolbar
  opacity: 0.98,
  backdropFilter: 'blur(8px)',
  color: editorTheme?.text || (theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b'),
}));

const ToolbarSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '2px', // Tight spacing within sections
  padding: '0 8px', // Section padding
  '&:not(:last-child)::after': {
    content: '""',
    width: '1px',
    height: '20px',
    backgroundColor: 'rgba(0,0,0,0.08)', // Subtle separator
    marginLeft: '12px',
    marginRight: '4px'
  }
}));

const StyledIconButton = styled(IconButton)(({ theme, editorTheme, active }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '8px', // Rounded buttons
  color: active ? editorTheme?.primary || '#007bff' : editorTheme?.textSecondary || '#666666',
  backgroundColor: active ? `${editorTheme?.primary || '#007bff'}10` : 'transparent',
  border: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: active 
      ? `${editorTheme?.primary || '#007bff'}18` 
      : `${editorTheme?.textSecondary || '#666666'}08`,
    color: active ? editorTheme?.primary || '#007bff' : editorTheme?.text || '#000000',
    transform: 'translateY(-1px)', // Subtle lift effect
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
  }
}));

// Clean, professional toolbar - no more ugly styled selects

const StyledButtonGroup = styled(ButtonGroup)(() => ({
  '& .MuiButtonGroup-grouped': {
    border: 'none',
    '&:not(:last-of-type)': {
      borderRight: 'none'
    }
  }
}));

const StyledTableContainer = styled('div')(({ theme }) => ({
  margin: '16px 0',
  overflowX: 'auto',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  maxWidth: '100%'
}));

const StyledTable = styled('table')(({ theme }) => ({
  borderCollapse: 'collapse',
  width: '100%',
  minWidth: '500px',
  border: `2px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  borderRadius: '8px',
  overflow: 'hidden'
}));

const StyledTableCell = styled('td')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: '12px 16px',
  textAlign: 'left',
  position: 'relative',
  minWidth: '120px',
  minHeight: '40px',
  transition: 'all 0.2s ease',
  wordWrap: 'break-word',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}20 !important`
  }
}));

const StyledTableHeader = styled('th')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: '12px 16px',
  background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
  fontWeight: '600',
  borderBottom: `2px solid ${theme.palette.divider}`,
  textAlign: 'left',
  position: 'relative',
  minWidth: '120px',
  resize: 'horizontal',
  overflow: 'hidden',
  color: theme.palette.text.primary
}));

const StyledCodeBlock = styled('pre')(({ theme }) => ({
  backgroundColor: `${theme.palette.grey[100]}`,
  padding: '16px',
  margin: 0,
  fontFamily: "'Courier New', monospace",
  fontSize: '14px',
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  whiteSpace: 'pre-wrap',
  overflowX: 'auto',
  borderRadius: '0 0 8px 8px'
}));

const StyledCodeHeader = styled('div')(({ theme }) => ({
  background: `${theme.palette.grey[200]}`,
  padding: '8px 16px',
  fontSize: '12px',
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontFamily: 'monospace',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '8px 8px 0 0'
}));

const RichTextToolbar = ({ 
  editorRef, 
  theme, 
  onCommand, 
  showAnalysisButtons = false, 
  onAnalyze, 
  isAnalyzing = false,
  isDark = false
}) => {
  const [linkAnchor, setLinkAnchor] = useState(null);
  const [imageAnchor, setImageAnchor] = useState(null);
  const [codeSnippetAnchor, setCodeSnippetAnchor] = useState(null);
  const [tableMenuAnchor, setTableMenuAnchor] = useState(null);
  const [selectedTableCell, setSelectedTableCell] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('file');
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [fontSize, setFontSize] = useState('16');
  const [fontFamily, setFontFamily] = useState('inherit');
  const [currentHeading, setCurrentHeading] = useState('p'); // Start with paragraph for natural writing
  const [isInCodeBlock, setIsInCodeBlock] = useState(false);
  
  // Beautiful custom text style selector state
  const [styleMenuOpen, setStyleMenuOpen] = useState(false);
  const [styleAnchor, setStyleAnchor] = useState(null);

  const getCurrentStyle = () => {
    return textStyles.find(style => style.value === currentHeading) || textStyles[0];
  };

  const handleStyleSelect = (styleValue) => {
    handleHeadingChange({ target: { value: styleValue } });
    setStyleMenuOpen(false);
    setStyleAnchor(null);
  };

  // Font size selector state
  const [fontSizeMenuOpen, setFontSizeMenuOpen] = useState(false);
  const [fontSizeAnchor, setFontSizeAnchor] = useState(null);

  // Language selector state
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [languageAnchor, setLanguageAnchor] = useState(null);

  // Font family selector state
  const [fontFamilyMenuOpen, setFontFamilyMenuOpen] = useState(false);
  const [fontFamilyAnchor, setFontFamilyAnchor] = useState(null);
  const [isInInlineCodeState, setIsInInlineCodeState] = useState(false);
  const fileInputRef = useRef(null);
  const timeoutRefs = useRef(new Set()); // Track active timeouts for cleanup

  // Custom hook for ripple effects
  const useRipple = () => {
    const createRipple = useCallback((event) => {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;
      
      const ripple = document.createElement('span');
      ripple.style.cssText = `
        position: absolute;
        border-radius: 50%;
        background: ${theme.primary}40;
        transform: scale(0);
        animation: ripple 0.6s linear;
        left: ${x}px;
        top: ${y}px;
        width: ${size}px;
        height: ${size}px;
        pointer-events: none;
      `;
      
      // Add keyframes if not already added
      if (!document.querySelector('#ripple-keyframes')) {
        const style = document.createElement('style');
        style.id = 'ripple-keyframes';
        style.textContent = `
          @keyframes ripple {
            to {
              transform: scale(2);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }
      
      button.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    }, [theme.primary]);
    
    return createRipple;
  };

  const createRipple = useRipple();
  const selectionUtils = useMemo(() => ({
    saveSelection: () => {
      try {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          return selection.getRangeAt(0).cloneRange();
        }
        return null;
      } catch (error) {
        console.warn('Failed to save selection:', error);
        return null;
      }
    },

    restoreSelection: (range) => {
      try {
        if (range) {
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          return true;
        }
        return false;
      } catch (error) {
        console.warn('Failed to restore selection:', error);
        return false;
      }
    },

    insertHTML: (html) => {
      try {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          
          const fragment = document.createDocumentFragment();
          const div = document.createElement('div');
          div.innerHTML = html;
          
          while (div.firstChild) {
            fragment.appendChild(div.firstChild);
          }
          
          range.insertNode(fragment);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          return true;
        }
        return false;
      } catch (error) {
        console.warn('Failed to insert HTML:', error);
        // Fallback to execCommand
        try {
          document.execCommand('insertHTML', false, html);
          return true;
        } catch (fallbackError) {
          console.error('Both custom and fallback HTML insertion failed:', fallbackError);
          return false;
        }
      }
    },

    applyFormat: (tagName, attributes = {}) => {
      try {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (!range.collapsed) {
            const element = document.createElement(tagName);
            Object.entries(attributes).forEach(([key, value]) => {
              element.setAttribute(key, value);
            });
            
            try {
              range.surroundContents(element);
              return true;
            } catch (error) {
              // If surroundContents fails, extract and wrap contents
              const contents = range.extractContents();
              element.appendChild(contents);
              range.insertNode(element);
              return true;
            }
          }
        }
        return false;
      } catch (error) {
        console.warn('Failed to apply format:', error);
        return false;
      }
    },

    formatBlock: (tagName) => {
      try {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          let element = range.commonAncestorContainer;
          
          // Find the block element
          while (element && element.nodeType !== Node.ELEMENT_NODE) {
            element = element.parentNode;
          }
          
          while (element && !['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'].includes(element.tagName)) {
            element = element.parentNode;
          }
          
          if (element && element !== editorRef.current) {
            const newElement = document.createElement(tagName);
            newElement.innerHTML = element.innerHTML;
            element.parentNode.replaceChild(newElement, element);
            return true;
          }
        }
        return false;
      } catch (error) {
        console.warn('Failed to format block:', error);
        // Fallback to execCommand
        try {
          document.execCommand('formatBlock', false, `<${tagName}>`);
          return true;
        } catch (fallbackError) {
          console.error('Both custom and fallback block formatting failed:', fallbackError);
          return false;
        }
      }
    }
  }), [editorRef]);

  // Memoized font families - Keep only essential document writing fonts
  const fontFamilies = useMemo(() => [
    { name: 'Default', value: 'inherit' },
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Times New Roman', value: 'Times New Roman, serif' },
    { name: 'Courier New', value: 'Courier New, monospace' }
  ], []);

  // Memoized font sizes - Keep only essential sizes
  const fontSizes = useMemo(() => [
    '12', '14', '16', '18'
  ], []);

  // Memoized programming languages
  const programmingLanguages = useMemo(() => [
    { name: 'JavaScript', value: 'javascript' },
    { name: 'Python', value: 'python' },
    { name: 'Java', value: 'java' },
    { name: 'C++', value: 'cpp' },
    { name: 'C#', value: 'csharp' },
    { name: 'PHP', value: 'php' },
    { name: 'Ruby', value: 'ruby' },
    { name: 'Go', value: 'go' },
    { name: 'Rust', value: 'rust' },
    { name: 'TypeScript', value: 'typescript' },
    { name: 'HTML', value: 'html' },
    { name: 'CSS', value: 'css' },
    { name: 'SQL', value: 'sql' },
    { name: 'Bash', value: 'bash' },
    { name: 'JSON', value: 'json' },
    { name: 'XML', value: 'xml' },
    { name: 'YAML', value: 'yaml' },
    { name: 'Markdown', value: 'markdown' },
    { name: 'Plain Text', value: 'text' }
  ], []);

  // Enable CSS styling for execCommand with error handling
  useEffect(() => {
    try {
      document.execCommand('styleWithCSS', false, true);
    } catch (error) {
      console.warn('Failed to enable CSS styling for execCommand:', error);
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all tracked timeouts
      timeoutRefs.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeoutRefs.current.clear();
    };
  }, []);

  // Cleanup table event listeners on unmount
  useEffect(() => {
    return () => {
      // Clean up all table event listeners
      if (editorRef.current) {
        const tables = editorRef.current.querySelectorAll('table');
        tables.forEach(table => {
          if (table._cleanupTableListeners) {
            table._cleanupTableListeners();
          }
        });
      }
    };
  }, []);

  // Table manipulation helper functions with proper cleanup
  const handleDeleteTable = useCallback((tableId) => {
    const table = document.getElementById(tableId);
    if (table) {
      // Clean up event listeners before removing the table
      if (table._cleanupTableListeners) {
        table._cleanupTableListeners();
      }
      
      const container = table.closest('.table-container');
      if (container) {
        container.remove();
      }
    }
    setTableMenuAnchor(null);
  }, []);

  const handleInsertRowAbove = useCallback((cell, table) => {
    const row = cell.parentNode;
    const newRow = row.cloneNode(true);
    const cells = newRow.querySelectorAll('td, th');
    cells.forEach((cell) => {
      if (cell.tagName === 'TH') {
        const td = document.createElement('td');
        td.className = 'table-cell';
        td.contentEditable = true;
        td.textContent = '';
        td.style.cssText = `
          border: 1px solid ${theme.textSecondary}40; 
          padding: 12px 16px; 
          background: ${theme.surface};
          transition: background-color 0.2s;
          min-height: 40px;
          color: ${theme.text};
        `;
        cell.parentNode.replaceChild(td, cell);
      } else {
        cell.textContent = '';
      }
    });
    row.parentNode.insertBefore(newRow, row);
    setTableMenuAnchor(null);
  }, [theme]);

  const handleInsertRowBelow = useCallback((cell, table) => {
    const row = cell.parentNode;
    const newRow = row.cloneNode(true);
    const cells = newRow.querySelectorAll('td, th');
    cells.forEach((cell) => {
      if (cell.tagName === 'TH') {
        const td = document.createElement('td');
        td.className = 'table-cell';
        td.contentEditable = true;
        td.textContent = '';
        td.style.cssText = `
          border: 1px solid ${theme.textSecondary}40; 
          padding: 12px 16px; 
          background: ${theme.surface};
          transition: background-color 0.2s;
          min-height: 40px;
          color: ${theme.text};
        `;
        cell.parentNode.replaceChild(td, cell);
      } else {
        cell.textContent = '';
      }
    });
    row.parentNode.insertBefore(newRow, row.nextSibling);
    setTableMenuAnchor(null);
  }, [theme]);

  const handleInsertColumnLeft = useCallback((cell, table) => {
    const cellIndex = Array.from(cell.parentNode.children).indexOf(cell);
    const rows = table.querySelectorAll('tr');
    rows.forEach((row, rowIndex) => {
      const newCell = rowIndex === 0 && row.parentNode.tagName === 'THEAD' 
        ? document.createElement('th')
        : document.createElement('td');
      
      newCell.className = 'table-cell';
      newCell.contentEditable = true;
      newCell.textContent = '';
      
      if (newCell.tagName === 'TH') {
        newCell.style.cssText = `
          border: 1px solid ${theme.textSecondary}40; 
          padding: 12px 16px; 
          background: ${theme.textSecondary}15;
          font-weight: 600;
          text-align: left;
          position: relative;
          min-width: 120px;
          resize: horizontal;
          overflow: hidden;
          color: ${theme.text};
        `;
      } else {
        const isEvenRow = rowIndex % 2 === 0;
        newCell.style.cssText = `
          border: 1px solid ${theme.textSecondary}40; 
          padding: 12px 16px; 
          background: ${isEvenRow ? theme.surface : theme.textSecondary + '05'};
          transition: background-color 0.2s;
          min-height: 40px;
          color: ${theme.text};
        `;
      }
      
      row.insertBefore(newCell, row.children[cellIndex]);
    });
    setTableMenuAnchor(null);
  }, [theme]);

  const handleInsertColumnRight = useCallback((cell, table) => {
    const cellIndex = Array.from(cell.parentNode.children).indexOf(cell);
    const rows = table.querySelectorAll('tr');
    rows.forEach((row, rowIndex) => {
      const newCell = rowIndex === 0 && row.parentNode.tagName === 'THEAD' 
        ? document.createElement('th')
        : document.createElement('td');
      
      newCell.className = 'table-cell';
      newCell.contentEditable = true;
      newCell.textContent = '';
      
      if (newCell.tagName === 'TH') {
        newCell.style.cssText = `
          border: 1px solid ${theme.textSecondary}40; 
          padding: 12px 16px; 
          background: ${theme.textSecondary}15;
          font-weight: 600;
          text-align: left;
          position: relative;
          min-width: 120px;
          resize: horizontal;
          overflow: hidden;
          color: ${theme.text};
        `;
      } else {
        const isEvenRow = rowIndex % 2 === 0;
        newCell.style.cssText = `
          border: 1px solid ${theme.textSecondary}40; 
          padding: 12px 16px; 
          background: ${isEvenRow ? theme.surface : theme.textSecondary + '05'};
          transition: background-color 0.2s;
          min-height: 40px;
          color: ${theme.text};
        `;
      }
      
      row.insertBefore(newCell, row.children[cellIndex + 1]);
    });
    setTableMenuAnchor(null);
  }, [theme]);

  const handleDeleteRow = useCallback((cell, table) => {
    const row = cell.parentNode;
    const tbody = row.parentNode;
    if (tbody.children.length > 1) {
      row.remove();
    }
    setTableMenuAnchor(null);
  }, []);

  const handleDeleteColumn = useCallback((cell, table) => {
    const cellIndex = Array.from(cell.parentNode.children).indexOf(cell);
    const rows = table.querySelectorAll('tr');
    
    // Check if table has more than 1 column
    if (rows[0].children.length > 1) {
      rows.forEach(row => {
        if (row.children[cellIndex]) {
          row.children[cellIndex].remove();
        }
      });
    }
    setTableMenuAnchor(null);
  }, []);

  // Code formatting functions
  const formatJavaScript = useCallback((code) => {
    // Basic JavaScript formatting
    let formatted = code
      .replace(/;/g, ';\n')
      .replace(/\{/g, ' {\n')
      .replace(/\}/g, '\n}\n')
      .replace(/,/g, ',\n')
      .replace(/\n\s*\n/g, '\n');
    
    // Add proper indentation
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indentSize = 2;
    
    return lines.map(line => {
      const trimmed = line.trim();
      if (trimmed === '') return '';
      
      if (trimmed.includes('}')) indentLevel = Math.max(0, indentLevel - 1);
      const indented = ' '.repeat(indentLevel * indentSize) + trimmed;
      if (trimmed.includes('{')) indentLevel++;
      
      return indented;
    }).join('\n');
  }, []);

  const formatPython = useCallback((code) => {
    // Basic Python formatting
    return code
      .replace(/;/g, '\n')
      .replace(/:/g, ':\n')
      .replace(/\n\s*\n/g, '\n')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n');
  }, []);

  const formatJSON = useCallback((code) => {
    try {
      const parsed = JSON.parse(code);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return code; // Return original if not valid JSON
    }
  }, []);

  const formatHTML = useCallback((code) => {
    // Basic HTML formatting
    let formatted = code
      .replace(/></g, '>\n<')
      .replace(/\n\s*\n/g, '\n');
    
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indentSize = 2;
    
    return lines.map(line => {
      const trimmed = line.trim();
      if (trimmed === '') return '';
      
      if (trimmed.startsWith('</')) indentLevel = Math.max(0, indentLevel - 1);
      const indented = ' '.repeat(indentLevel * indentSize) + trimmed;
      if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
        indentLevel++;
      }
      
      return indented;
    }).join('\n');
  }, []);

  const formatCSS = useCallback((code) => {
    // Basic CSS formatting
    return code
      .replace(/\{/g, ' {\n  ')
      .replace(/;/g, ';\n  ')
      .replace(/\}/g, '\n}\n')
      .replace(/,/g, ',\n')
      .replace(/\n\s*\n/g, '\n');
  }, []);

  const formatCode = useCallback((code, language) => {
    switch (language) {
      case 'javascript':
      case 'typescript':
      case 'java':
      case 'cpp':
      case 'csharp':
        return formatJavaScript(code);
      case 'python':
        return formatPython(code);
      case 'json':
        return formatJSON(code);
      case 'html':
      case 'xml':
        return formatHTML(code);
      case 'css':
        return formatCSS(code);
      default:
        // For other languages, just clean up whitespace
        return code
          .split('\n')
          .map(line => line.trim())
          .join('\n')
          .replace(/\n\s*\n/g, '\n');
    }
  }, [formatJavaScript, formatPython, formatJSON, formatHTML, formatCSS]);

  // Local code block formatting function (replaces global window.formatCodeBlock)
  const handleFormatCodeBlock = useCallback((codeElement) => {
    if (codeElement && codeElement.tagName === 'PRE') {
      const language = codeElement.getAttribute('data-language') || 'text';
      const currentCode = codeElement.textContent;
      const formattedCode = formatCode(currentCode, language);
      codeElement.textContent = formattedCode;
      
      // Find and update the format button if it exists
      const container = codeElement.parentElement;
      const formatButton = container?.querySelector('button');
      if (formatButton) {
        const originalText = formatButton.textContent;
        formatButton.textContent = '✓ Formatted';
        formatButton.style.background = '#4caf50';
        setTimeout(() => {
          formatButton.textContent = originalText;
          formatButton.style.background = '';
        }, 2000);
      }
    }
  }, [formatCode]);

  // Set up event delegation for format buttons
  useEffect(() => {
    const handleFormatButtonClick = (e) => {
      if (e.target.classList.contains('format-code-btn')) {
        e.preventDefault();
        const codeBlock = e.target.closest('div').nextElementSibling;
        if (codeBlock && codeBlock.tagName === 'PRE') {
          handleFormatCodeBlock(codeBlock);
        }
      }
    };

    if (editorRef.current) {
      editorRef.current.addEventListener('click', handleFormatButtonClick);
      return () => {
        if (editorRef.current) {
          editorRef.current.removeEventListener('click', handleFormatButtonClick);
        }
      };
    }
  }, [handleFormatCodeBlock]);
  // Handle table insertion with improved styling and error handling
  const handleTableInsert = useCallback(() => {
    if (!editorRef.current) {
      console.warn('Editor ref not available for table insertion');
      return;
    }

    try {
      const tableId = `table-${Date.now()}`;
      
      const tableStyles = {
        container: `margin: 16px 0; overflow-x: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); max-width: 100%;`,
        table: `border-collapse: collapse; width: 100%; min-width: 500px; border: 2px solid ${theme.textSecondary}40; background: ${theme.surface}; box-shadow: 0 2px 4px rgba(0,0,0,0.1);`,
        headerCell: `border: 1px solid ${theme.textSecondary}40; padding: 12px 16px; background: ${theme.textSecondary}15; font-weight: 600; text-align: left; position: relative; min-width: 120px; color: ${theme.text};`,
        bodyCell: `border: 1px solid ${theme.textSecondary}40; padding: 12px 16px; background: ${theme.surface}; transition: background-color 0.2s; min-height: 40px; color: ${theme.text};`,
        bodyCellAlt: `border: 1px solid ${theme.textSecondary}40; padding: 12px 16px; background: ${theme.textSecondary}05; transition: background-color 0.2s; min-height: 40px; color: ${theme.text};`
      };

      const tableHTML = [
        `<div class="table-container" style="${tableStyles.container}">`,
          `<table id="${tableId}" class="editable-table" style="${tableStyles.table}">`,
            '<thead>',
              '<tr>',
                `<th class="table-cell" contenteditable="true" style="${tableStyles.headerCell}"></th>`,
                `<th class="table-cell" contenteditable="true" style="${tableStyles.headerCell}"></th>`,
                `<th class="table-cell" contenteditable="true" style="${tableStyles.headerCell}"></th>`,
              '</tr>',
            '</thead>',
            '<tbody>',
              '<tr>',
                `<td class="table-cell" contenteditable="true" style="${tableStyles.bodyCell}"></td>`,
                `<td class="table-cell" contenteditable="true" style="${tableStyles.bodyCell}"></td>`,
                `<td class="table-cell" contenteditable="true" style="${tableStyles.bodyCell}"></td>`,
              '</tr>',
              '<tr>',
                `<td class="table-cell" contenteditable="true" style="${tableStyles.bodyCellAlt}"></td>`,
                `<td class="table-cell" contenteditable="true" style="${tableStyles.bodyCellAlt}"></td>`,
                `<td class="table-cell" contenteditable="true" style="${tableStyles.bodyCellAlt}"></td>`,
              '</tr>',
            '</tbody>',
          '</table>',
        '</div>',
        '<p><br></p>'
      ].join('');
      
      // Use custom selection management with fallback
      const success = selectionUtils.insertHTML(tableHTML);
      
      if (success) {
        // Add event listeners for context menu after insertion
        const timeoutId = setTimeout(() => {
          setupTableContextMenu(tableId);
          timeoutRefs.current.delete(timeoutId); // Remove from tracking after execution
        }, 100);
        timeoutRefs.current.add(timeoutId); // Track timeout for cleanup
        
        editorRef.current.focus();
      } else {
        console.error('Failed to insert table');
      }
    } catch (error) {
      console.error('Error inserting table:', error);
    }
  }, [theme, selectionUtils]);

  // Setup context menu for table with proper cleanup tracking
  const setupTableContextMenu = useCallback((tableId) => {
    const table = document.getElementById(tableId);
    if (!table) return;

    const cells = table.querySelectorAll('td, th');
    const cleanupFunctions = [];

    cells.forEach(cell => {
      const contextMenuHandler = (e) => {
        e.preventDefault();
        setSelectedTableCell({ cell, table });
        setTableMenuAnchor(e.currentTarget);
      };

      const mouseEnterHandler = () => {
        cell.style.backgroundColor = `${theme.primary}20`;
      };

      const mouseLeaveHandler = () => {
        const isEvenRow = Array.from(cell.parentNode.parentNode.children).indexOf(cell.parentNode) % 2 === 0;
        const isHeader = cell.tagName === 'TH';
        if (isHeader) {
          cell.style.background = `${theme.textSecondary}15`;
        } else {
          cell.style.backgroundColor = isEvenRow ? theme.surface : `${theme.textSecondary}05`;
        }
      };

      // Add event listeners
      cell.addEventListener('contextmenu', contextMenuHandler);
      cell.addEventListener('mouseenter', mouseEnterHandler);
      cell.addEventListener('mouseleave', mouseLeaveHandler);

      // Store cleanup functions
      cleanupFunctions.push(() => {
        cell.removeEventListener('contextmenu', contextMenuHandler);
        cell.removeEventListener('mouseenter', mouseEnterHandler);
        cell.removeEventListener('mouseleave', mouseLeaveHandler);
      });
    });

    // Store cleanup functions on the table element for later cleanup
    table._cleanupTableListeners = () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [theme]);

  // Handle checklist insertion - Simple checklist like bullet list
  // Handle checklist insertion with error handling
  const handleChecklistInsert = useCallback(() => {
    if (!editorRef.current) {
      console.warn('Editor ref not available for checklist insertion');
      return;
    }

    try {
      editorRef.current.focus();
      
      const checklistHTML = [
        '<div>',
          '<div style="display: flex; align-items: center; margin: 2px 0;">',
            '<input type="checkbox" style="margin-right: 8px;" />',
            '<span contenteditable="true" style="outline: none;"></span>',
          '</div>',
          '<div style="display: flex; align-items: center; margin: 2px 0;">',
            '<input type="checkbox" style="margin-right: 8px;" />',
            '<span contenteditable="true" style="outline: none;"></span>',
          '</div>',
          '<div style="display: flex; align-items: center; margin: 2px 0;">',
            '<input type="checkbox" style="margin-right: 8px;" />',
            '<span contenteditable="true" style="outline: none;"></span>',
          '</div>',
        '</div>',
        '<p><br></p>'
      ].join('');
      
      // Use custom selection management with fallback
      const success = selectionUtils.insertHTML(checklistHTML);
      
      if (!success) {
        console.error('Failed to insert checklist');
      }
    } catch (error) {
      console.error('Error inserting checklist:', error);
    }
  }, [selectionUtils]);

  // Check if current selection is within inline code
  const isInInlineCode = useCallback(() => {
    if (!editorRef.current) return false;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return false;
    
    let node = selection.anchorNode;
    
    // Traverse up to find if we're in a code element
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'code') {
        return true;
      }
      node = node.parentNode;
    }
    
    return false;
  }, []);

  // Remove inline code formatting
  const removeInlineCode = useCallback(() => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    let node = selection.anchorNode;
    
    // Find the code element
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'code') {
        const codeElement = node;
        const textContent = codeElement.textContent;
        
        // Replace code element with plain text
        const textNode = document.createTextNode(textContent);
        codeElement.parentNode.replaceChild(textNode, codeElement);
        
        // Place cursor after the text
        const range = document.createRange();
        range.setStartAfter(textNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        break;
      }
      node = node.parentNode;
    }
  }, []);

  // Handle inline code insertion with improved error handling
  const handleInlineCode = useCallback(() => {
    if (!editorRef.current) {
      console.warn('Editor ref not available for inline code');
      return;
    }
    
    try {
      // Check if we're already in inline code - if so, remove formatting
      if (isInInlineCode()) {
        removeInlineCode();
        return;
      }
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = selection.toString();
        
        // Define inline code styles using theme
        const codeStyles = `
          background-color: ${theme.textSecondary}15; 
          padding: 2px 6px; 
          border-radius: 4px; 
          font-family: 'Courier New', 'Monaco', 'Menlo', monospace; 
          font-size: 0.9em;
          color: ${theme.text};
          border: 1px solid ${theme.textSecondary}20;
        `;
        
        if (selectedText.trim()) {
          // If text is selected, wrap it in inline code formatting
          // First check if the selection is already wrapped in code
          const parentElement = range.commonAncestorContainer.parentElement;
          if (parentElement && parentElement.tagName.toLowerCase() === 'code') {
            // Already in code, remove formatting
            removeInlineCode();
            return;
          }
          
          const success = selectionUtils.applyFormat('code', { style: codeStyles });
          
          if (!success) {
            // Fallback to manual insertion
            const codeHTML = `<code style="${codeStyles}">${selectedText}</code>`;
            selectionUtils.insertHTML(codeHTML);
          }
        } else {
          // If no text is selected, create an inline code element ready for typing
          const codeHTML = `<code style="${codeStyles}" contenteditable="true">\u200B</code>`;
          const success = selectionUtils.insertHTML(codeHTML);
          
          if (success) {
            // Find the inserted code element and set up event listeners
            const timeoutId = setTimeout(() => {
              const codeElements = editorRef.current.querySelectorAll('code');
              const lastCodeElement = codeElements[codeElements.length - 1];
              
              if (lastCodeElement && lastCodeElement.textContent === '\u200B') {
                // Place cursor inside the code element
                const newRange = document.createRange();
                newRange.selectNodeContents(lastCodeElement);
                newRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(newRange);
                
                // Add event listeners for cleanup
                const handleCodeComplete = (e) => {
                  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Tab' || e.key === 'Enter') {
                    if (lastCodeElement.textContent === '\u200B') {
                      lastCodeElement.textContent = '';
                    }
                    lastCodeElement.removeEventListener('keydown', handleCodeComplete);
                  }
                };
                
                const handleBlur = () => {
                  if (lastCodeElement.textContent === '\u200B') {
                    lastCodeElement.textContent = '';
                  }
                  lastCodeElement.removeEventListener('blur', handleBlur);
                };
                
                lastCodeElement.addEventListener('keydown', handleCodeComplete);
                lastCodeElement.addEventListener('blur', handleBlur);
              }
              timeoutRefs.current.delete(timeoutId); // Remove from tracking after execution
            }, 10);
            timeoutRefs.current.add(timeoutId); // Track timeout for cleanup
          }
        }
        
        editorRef.current.focus();
      }
    } catch (error) {
      console.error('Error handling inline code:', error);
    }
  }, [theme, isInInlineCode, removeInlineCode, selectionUtils]);

  // Detect current heading level at cursor position
  const getCurrentHeadingLevel = useCallback(() => {
    if (!editorRef.current) return 'p';
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return 'p';
    
    let node = selection.anchorNode;
    
    // If the node is a text node, get its parent element
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }
    
    // Traverse up to find the block element
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        
        // Check for regular headings and block elements
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre'].includes(tagName)) {
          return tagName;
        }
        
        // All paragraphs (indented or not) should show as 'p' in dropdown
        if (tagName === 'p') {
          return 'p';
        }
      }
      node = node.parentNode;
    }
    
    return 'p';
  }, []);

  // Detect if cursor is in a code block
  const checkIfInCodeBlock = useCallback(() => {
    if (!editorRef.current) return false;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return false;
    
    let node = selection.anchorNode;
    
    // Traverse up to find if we're in a code block
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        if (tagName === 'pre') {
          return true;
        }
      }
      node = node.parentNode;
    }
    
    return false;
  }, []);

  // Format code in current code block
  const handleFormatCurrentCodeBlock = useCallback(() => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    let node = selection.anchorNode;
    
    // Find the code block element
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'pre') {
        handleFormatCodeBlock(node);
        
        // Restore cursor position
        const range = document.createRange();
        range.setStart(node, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        return;
      }
      node = node.parentNode;
    }
  }, [handleFormatCodeBlock]);

  // Add keyboard shortcut support for inline code and auto-formatting
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+` or Cmd+` for inline code
      if ((e.ctrlKey || e.metaKey) && e.key === '`') {
        e.preventDefault();
        handleInlineCode();
      }
      
      // Fixed Markdown shortcuts - only affect the paragraph where you're typing
      if (e.key === ' ' && editorRef.current) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          
          // Find the immediate paragraph or block element containing the cursor
          let currentParagraph = range.startContainer;
          
          // If we're in a text node, get its parent element
          if (currentParagraph.nodeType === Node.TEXT_NODE) {
            currentParagraph = currentParagraph.parentElement;
          }
          
          // Find the closest block-level element (P, DIV, etc.)
          while (currentParagraph && 
                 currentParagraph !== editorRef.current && 
                 !['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(currentParagraph.tagName)) {
            currentParagraph = currentParagraph.parentElement;
          }
          
          if (currentParagraph && currentParagraph !== editorRef.current) {
            // Get only the text content of this specific paragraph
            const paragraphText = currentParagraph.textContent || '';
            
            // Check if this paragraph starts with heading markdown
            let headingTag = null;
            
            if (paragraphText === '#') {
              headingTag = 'H1';
            } else if (paragraphText === '##') {
              headingTag = 'H2';
            } else if (paragraphText === '###') {
              headingTag = 'H3';
            } else if (paragraphText === '####') {
              headingTag = 'H4';
            } else if (paragraphText === '#####') {
              headingTag = 'H5';
            }
            
            if (headingTag) {
              e.preventDefault();
              
              // Clear this paragraph's content
              currentParagraph.textContent = '';
              
              // Convert this paragraph to heading
              const newHeading = document.createElement(headingTag.toLowerCase());
              currentParagraph.parentNode.replaceChild(newHeading, currentParagraph);
              
              // Position cursor in the new heading
              const newRange = document.createRange();
              newRange.setStart(newHeading, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
              
              return;
            }
          }
        }
      }
      
      // Inline code with backticks: `text` -> <code>text</code>
      if (e.key === '`' && editorRef.current) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const textNode = range.startContainer;
          
          if (textNode.nodeType === Node.TEXT_NODE) {
            const textContent = textNode.textContent;
            const cursorPos = range.startOffset;
            
            // Look for opening backtick
            const beforeCursor = textContent.substring(0, cursorPos);
            const lastBacktick = beforeCursor.lastIndexOf('`');
            
            if (lastBacktick !== -1) {
              // Found opening backtick, create inline code
              const codeText = beforeCursor.substring(lastBacktick + 1);
              
              if (codeText.length > 0) {
                e.preventDefault();
                
                // Remove the opening backtick and the text
                const newRange = document.createRange();
                newRange.setStart(textNode, lastBacktick);
                newRange.setEnd(textNode, cursorPos);
                newRange.deleteContents();
                
                // Create inline code element
                const codeStyles = `
                  background-color: ${theme.textSecondary}15; 
                  padding: 2px 6px; 
                  border-radius: 4px; 
                  font-family: 'Courier New', monospace; 
                  font-size: 0.9em;
                  color: ${theme.text};
                  border: 1px solid ${theme.textSecondary}25;
                `;
                
                const codeHTML = `<code style="${codeStyles}">${codeText}</code>`;
                selectionUtils.insertHTML(codeHTML);
                return;
              }
            }
          }
        }
      }
      
      // Enhanced Tab handling for lists with different bullet styles
      if (e.key === 'Tab' && editorRef.current) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          let currentElement = range.startContainer;
          
          // Find the closest list item
          while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
            currentElement = currentElement.parentNode;
          }
          
          while (currentElement && currentElement.tagName !== 'LI' && currentElement !== editorRef.current) {
            currentElement = currentElement.parentNode;
          }
          
          if (currentElement && currentElement.tagName === 'LI') {
            e.preventDefault();
            
            if (e.shiftKey) {
              // Shift+Tab: Decrease indentation
              document.execCommand('outdent', false, null);
            } else {
              // Tab: Increase indentation with custom bullet styles
              document.execCommand('indent', false, null);
              
              // Apply custom bullet styles based on nesting level
              setTimeout(() => {
                const listItem = currentElement;
                const parentList = listItem.parentNode;
                let nestingLevel = 0;
                
                // Count nesting level
                let parent = parentList;
                while (parent && parent !== editorRef.current) {
                  if (parent.tagName === 'UL' || parent.tagName === 'OL') {
                    nestingLevel++;
                  }
                  parent = parent.parentNode;
                }
                
                // Apply different bullet styles based on nesting level
                if (parentList && parentList.tagName === 'UL') {
                  const bulletStyles = [
                    'disc',     // Level 0: •
                    'circle',   // Level 1: ○
                    'square'    // Level 2: ■
                  ];
                  
                  const style = bulletStyles[nestingLevel % bulletStyles.length];
                  parentList.style.listStyleType = style;
                }
              }, 10);
            }
            return;
          }
        }
      }
    };

    if (editorRef.current) {
      editorRef.current.addEventListener('keydown', handleKeyDown);
      return () => {
        if (editorRef.current) {
          editorRef.current.removeEventListener('keydown', handleKeyDown);
        }
      };
    }
  }, [handleInlineCode]);

  // Update heading state when cursor moves
  useEffect(() => {
    let timeoutId;
    
    const updateHeadingState = () => {
      // Clear any pending updates
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Throttle updates to prevent excessive calls
      timeoutId = setTimeout(() => {
        const currentLevel = getCurrentHeadingLevel();
        const inCodeBlock = checkIfInCodeBlock();
        const inInlineCode = isInInlineCode();
        
        // Only update if there's actually a change
        if (currentLevel !== currentHeading) {
          console.log(`🔄 Heading state changed from ${currentHeading} to ${currentLevel}`);
          setCurrentHeading(currentLevel);
        }
        
        setIsInCodeBlock(inCodeBlock);
        setIsInInlineCodeState(inInlineCode);
      }, 100); // 100ms throttle
    };

    if (editorRef.current) {
      editorRef.current.addEventListener('keyup', updateHeadingState);
      editorRef.current.addEventListener('mouseup', updateHeadingState);
      editorRef.current.addEventListener('focus', updateHeadingState);
      editorRef.current.addEventListener('headingChanged', updateHeadingState);
      
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (editorRef.current) {
          editorRef.current.removeEventListener('keyup', updateHeadingState);
          editorRef.current.removeEventListener('mouseup', updateHeadingState);
          editorRef.current.removeEventListener('focus', updateHeadingState);
          editorRef.current.removeEventListener('headingChanged', updateHeadingState);
        }
      };
    }
  }, [getCurrentHeadingLevel, checkIfInCodeBlock, isInInlineCode, currentHeading]);

  // Handle heading change with error handling and custom selection management
  const handleHeadingChange = useCallback((event) => {
    const formatType = event.target.value;
    console.log(`🎯 Heading change requested: ${formatType}`); // Enhanced debug log
    setCurrentHeading(formatType);
    
    if (!editorRef.current) {
      console.warn('Editor ref not available for heading change');
      return;
    }

    try {
      // Focus the editor first
      editorRef.current.focus();
      
      // Apply the format to the current selection or cursor position
      if (formatType === 'p') {
        console.log('📝 Formatting as paragraph'); // Enhanced debug log
        
        // For paragraph formatting, we need to remove any indentation ONLY when manually selected
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          let node = selection.anchorNode;
          
          // If the node is a text node, get its parent element
          if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentNode;
          }
          
          // Find the paragraph element
          while (node && node !== editorRef.current) {
            if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'p') {
              // Only remove indentation when user manually selects "Paragraph" from dropdown
              // This means they want to reset to a normal paragraph
              console.log('🔄 Removing indentation for manual paragraph selection');
              node.style.marginLeft = '';
              node.style.paddingLeft = '';
              break;
            }
            node = node.parentNode;
          }
        }
        
        const success = selectionUtils.formatBlock('p');
        if (!success) {
          console.log('⚡ Using fallback for paragraph formatting');
          document.execCommand('formatBlock', false, '<p>');
        } else {
          console.log('✅ Paragraph formatting successful');
        }
      } else if (formatType === 'blockquote') {
        console.log('💬 Formatting as blockquote'); // Enhanced debug log
        const success = selectionUtils.formatBlock('blockquote');
        if (!success) {
          console.log('⚡ Using fallback for blockquote formatting');
          document.execCommand('formatBlock', false, '<blockquote>');
        } else {
          console.log('✅ Blockquote formatting successful');
        }
      } else if (formatType === 'pre') {
        console.log('💻 Formatting as code block'); // Enhanced debug log
        // Create a more advanced code block with format button
        const codeStyles = {
          container: `margin: 16px 0; border-radius: 8px; overflow: hidden; border: 1px solid ${theme.textSecondary}30;`,
          header: `background: ${theme.textSecondary}15; padding: 8px 16px; font-size: 12px; color: ${theme.textSecondary}; border-bottom: 1px solid ${theme.textSecondary}20; font-family: monospace; display: flex; justify-content: space-between; align-items: center;`,
          button: `background: ${theme.primary}; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px;`,
          code: `background: ${theme.textSecondary}08; padding: 16px; margin: 0; font-family: 'Courier New', monospace; font-size: 14px; line-height: 1.4; color: ${theme.text}; white-space: pre; overflow-x: auto;`
        };

        const codeBlockHTML = [
          `<div style="${codeStyles.container}">`,
            `<div style="${codeStyles.header}">`,
              '<span>Code Block</span>',
              `<div style="display: flex; align-items: center; gap: 8px;">`,
                `<span style="font-size: 10px; opacity: 0.7;">Shift+Enter to exit</span>`,
                `<button class="format-code-btn" style="${codeStyles.button}">Format</button>`,
              `</div>`,
            '</div>',
            `<pre style="${codeStyles.code}" contenteditable="true" data-language="text">// Enter your code here</pre>`,
          '</div>',
          '<p><br></p>'
        ].join('');
        
        const success = selectionUtils.insertHTML(codeBlockHTML);
        if (!success) {
          console.error('❌ Failed to insert code block');
        } else {
          console.log('✅ Code block inserted successfully');
        }
      } else {
        // Handle headings (h1, h2, h3, h4) - Always use execCommand for reliability
        console.log(`📋 Formatting as ${formatType.toUpperCase()}`); // Enhanced debug log
        
        // Always use execCommand for headings as it's more reliable
        try {
          document.execCommand('formatBlock', false, `<${formatType}>`);
          console.log(`✅ ${formatType.toUpperCase()} formatting applied via execCommand`);
          
          // Ensure cursor is positioned correctly after formatting
          setTimeout(() => {
            if (editorRef.current) {
              editorRef.current.focus();
              const selection = window.getSelection();
              if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.collapse(false); // Move cursor to end
                selection.removeAllRanges();
                selection.addRange(range);
              }
            }
          }, 10);
          
        } catch (error) {
          console.error(`❌ Failed to format as ${formatType}:`, error);
        }
        
        // Force update the heading state with a longer delay to allow DOM to update
        setTimeout(() => {
          const currentLevel = getCurrentHeadingLevel();
          console.log(`🔄 Current level after formatting: ${currentLevel}`);
          if (currentLevel !== formatType) {
            console.log(`⚠️ Level mismatch, forcing update to ${formatType}`);
            setCurrentHeading(formatType);
          }
        }, 150); // Increased delay
      }
      
      // Trigger custom event for toolbar to update heading state
      setTimeout(() => {
        if (editorRef.current) {
          const event = new CustomEvent('headingChanged');
          editorRef.current.dispatchEvent(event);
          console.log('🔄 Heading state updated');
        }
      }, 100);
      
      editorRef.current.focus();
    } catch (error) {
      console.error('❌ Error changing heading format:', error);
    }
  }, [theme, selectionUtils]);

  // Execute formatting command with error handling
  const executeCommand = useCallback((command, value = null) => {
    if (!editorRef.current) {
      console.warn('Editor ref not available for command execution');
      return;
    }

    try {
      editorRef.current.focus();
      
      // Use modern approaches for common commands
      if (command === 'bold' || command === 'italic' || command === 'underline') {
        const tagName = command === 'bold' ? 'strong' : command === 'italic' ? 'em' : 'u';
        const success = selectionUtils.applyFormat(tagName);
        
        if (!success) {
          // Fallback to execCommand
          document.execCommand(command, false, value);
        }
      } else {
        // Use execCommand for other commands with error handling
        const success = document.execCommand(command, false, value);
        if (!success) {
          console.warn(`Command ${command} may not be supported or failed`);
        }
      }
      
      onCommand && onCommand(command, value);
    } catch (error) {
      console.error(`Error executing command ${command}:`, error);
    }
  }, [onCommand, selectionUtils]);

  // Handle link insertion with error handling and custom selection management
  const handleLinkInsert = useCallback(() => {
    if (!linkUrl.trim()) {
      console.warn('No URL provided for link insertion');
      return;
    }

    if (!editorRef.current) {
      console.warn('Editor ref not available for link insertion');
      return;
    }

    try {
      // Focus the editor first to ensure cursor position is maintained
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const selectedText = selection.toString();
        let linkHTML;
        
        if (selectedText) {
          // If text is selected, wrap it in a link
          linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${selectedText}</a>`;
        } else {
          // If no text selected, insert link with URL as text
          linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkUrl}</a>`;
        }
        
        const success = selectionUtils.insertHTML(linkHTML);
        if (!success) {
          console.error('Failed to insert link');
        }
      } else {
        // Fallback: insert at current cursor position
        const linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkUrl}</a>`;
        const success = selectionUtils.insertHTML(linkHTML);
        if (!success) {
          console.error('Failed to insert link at cursor position');
        }
      }
      
      setLinkUrl('');
      setLinkAnchor(null);
    } catch (error) {
      console.error('Error inserting link:', error);
    }
  }, [linkUrl, selectionUtils]);

  // Handle image insertion with error handling and custom selection management
  const handleImageInsert = useCallback(() => {
    if (!editorRef.current) {
      console.warn('Editor ref not available for image insertion');
      return;
    }

    try {
      if (uploadMethod === 'file' && imageFile) {
        // Handle file upload
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const imageHTML = `<img src="${e.target.result}" alt="Uploaded image" style="max-width: 100%; height: auto; border-radius: 4px; margin: 8px 0;" />`;
            editorRef.current.focus();
            
            const success = selectionUtils.insertHTML(imageHTML);
            if (!success) {
              console.error('Failed to insert uploaded image');
            }
          } catch (error) {
            console.error('Error processing uploaded image:', error);
          }
        };
        
        reader.onerror = (error) => {
          console.error('Error reading image file:', error);
        };
        
        reader.readAsDataURL(imageFile);
        
        setImageFile(null);
        setImageAnchor(null);
      } else if (uploadMethod === 'url' && imageUrl.trim()) {
        // Handle URL insertion
        const imageHTML = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto; border-radius: 4px; margin: 8px 0;" />`;
        editorRef.current.focus();
        
        const success = selectionUtils.insertHTML(imageHTML);
        if (!success) {
          console.error('Failed to insert image from URL');
        }
        
        setImageUrl('');
        setImageAnchor(null);
      } else {
        console.warn('No valid image source provided');
      }
    } catch (error) {
      console.error('Error inserting image:', error);
    }
  }, [uploadMethod, imageFile, imageUrl, selectionUtils]);

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
    }
  }, []);

  // Handle code snippet insertion with error handling and custom selection management
  const handleCodeSnippetInsert = useCallback(() => {
    if (!editorRef.current) {
      console.warn('Editor ref not available for code snippet insertion');
      return;
    }

    try {
      const languageName = programmingLanguages.find(lang => lang.value === selectedLanguage)?.name || 'Code';
      
      const codeStyles = {
        container: `margin: 16px 0; border-radius: 8px; overflow: hidden; border: 1px solid ${theme.textSecondary}30;`,
        header: `background: ${theme.textSecondary}15; padding: 8px 16px; font-size: 12px; color: ${theme.textSecondary}; border-bottom: 1px solid ${theme.textSecondary}20; font-family: monospace; display: flex; justify-content: space-between; align-items: center;`,
        button: `background: ${theme.primary}; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px;`,
        code: `background: ${theme.textSecondary}08; padding: 16px; margin: 0; font-family: 'Courier New', monospace; font-size: 14px; line-height: 1.4; color: ${theme.text}; white-space: pre; overflow-x: auto;`
      };

      const codeSnippetHTML = [
        `<div style="${codeStyles.container}">`,
          `<div style="${codeStyles.header}">`,
            `<span>${languageName}</span>`,
            `<div style="display: flex; align-items: center; gap: 8px;">`,
              `<span style="font-size: 10px; opacity: 0.7;">Shift+Enter to exit</span>`,
              `<button class="format-code-btn" style="${codeStyles.button}">Format</button>`,
            `</div>`,
          '</div>',
          `<pre style="${codeStyles.code}" contenteditable="true" data-language="${selectedLanguage}">// Enter your ${selectedLanguage} code here</pre>`,
        '</div>',
        '<p><br></p>'
      ].join('');
      
      const success = selectionUtils.insertHTML(codeSnippetHTML);
      if (success) {
        setCodeSnippetAnchor(null);
        editorRef.current.focus();
      } else {
        console.error('Failed to insert code snippet');
      }
    } catch (error) {
      console.error('Error inserting code snippet:', error);
    }
  }, [theme, selectedLanguage, programmingLanguages, selectionUtils]);

  // Handle font size change with error handling
  const handleFontSizeChange = useCallback((size) => {
    setFontSize(size);
    if (editorRef.current) {
      editorRef.current.style.fontSize = `${size}px`;
    }
  }, []);

  // Handle font family change with error handling
  const handleFontFamilyChange = useCallback((family) => {
    setFontFamily(family);
    if (editorRef.current) {
      editorRef.current.style.fontFamily = family;
    }
  }, []);

  // Check if command is active
  const isCommandActive = useCallback((command) => {
    try {
      return document.queryCommandState(command);
    } catch (e) {
      return false;
    }
  }, []);

  // Compact ToolbarButton - saves vertical space
  const ToolbarButton = useMemo(() => React.memo(({ command, icon, tooltip, value, active }) => (
    <Tooltip title={tooltip} placement="top">
      <IconButton
        size="small"
        onClick={() => executeCommand(command, value)}
        sx={{
          width: '32px', // Smaller buttons
          height: '32px',
          borderRadius: '6px',
          color: (active || isCommandActive(command)) ? theme.primary : theme.textSecondary,
          backgroundColor: (active || isCommandActive(command)) ? `${theme.primary}15` : 'transparent',
          border: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: (active || isCommandActive(command)) 
              ? `${theme.primary}25` 
              : `${theme.textSecondary}10`,
            color: (active || isCommandActive(command)) ? theme.primary : theme.text,
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)' // Lighter shadow
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  )), [theme, executeCommand, isCommandActive]);
  return (
    <div>
      <ToolbarContainer>
        {/* Clean, Notion-inspired text style selector */}
        <Button
          onClick={(e) => {
            setStyleAnchor(e.currentTarget);
            setStyleMenuOpen(true);
          }}
          sx={{
            minWidth: 'auto',
            height: '32px',
            padding: '6px 8px',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            border: 'none',
            color: theme.text,
            fontSize: '14px',
            fontWeight: '400',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${theme.textSecondary}08`
            }
          }}
        >
          {getCurrentStyle().label}
        </Button>

        {/* Simple dropdown menu */}
        <Menu
          anchorEl={styleAnchor}
          open={styleMenuOpen}
          onClose={() => {
            setStyleMenuOpen(false);
            setStyleAnchor(null);
          }}
          PaperProps={{
            sx: {
              mt: 0.5,
              borderRadius: '6px',
              border: `1px solid ${theme.textSecondary}20`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              minWidth: '160px',
              backgroundColor: theme.surface
            }
          }}
        >
          {textStyles.map((style) => (
            <MenuItem
              key={style.value}
              onClick={() => handleStyleSelect(style.value)}
              sx={{
                fontSize: '14px',
                py: 1,
                px: 2,
                '&:hover': {
                  backgroundColor: `${theme.textSecondary}08`
                }
              }}
            >
              {style.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Compact divider */}
        <Box sx={{ width: '1px', height: '20px', backgroundColor: `${theme.textSecondary}15`, mx: 0.5 }} />

        {/* Undo/Redo */}
        <ButtonGroup size="small" sx={{ '& .MuiButtonGroup-grouped': { border: 'none' } }}>
          <ToolbarButton
            command="undo"
            icon={<Undo fontSize="small" />}
            tooltip="Undo (Ctrl+Z)"
          />
          <ToolbarButton
            command="redo"
            icon={<Redo fontSize="small" />}
            tooltip="Redo (Ctrl+Y)"
          />
        </ButtonGroup>

        {/* Subtle spacing */}
        <Box sx={{ width: '1px', height: '24px', backgroundColor: 'rgba(0,0,0,0.08)', mx: 1 }} />

        {/* Font Controls */}
        <Box display="flex" alignItems="center" gap={1}>
          {/* Clean Font Family Selector */}
          <Button
            onClick={(e) => {
              setFontFamilyAnchor(e.currentTarget);
              setFontFamilyMenuOpen(true);
            }}
            sx={{
              minWidth: 'auto',
              height: '32px',
              padding: '6px 8px',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.text,
              fontSize: '14px',
              fontWeight: '400',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${theme.textSecondary}08`
              }
            }}
          >
            {fontFamilies.find(font => font.value === fontFamily)?.name || 'Font'}
          </Button>

          {/* Font Family Menu */}
          <Menu
            anchorEl={fontFamilyAnchor}
            open={fontFamilyMenuOpen}
            onClose={() => {
              setFontFamilyMenuOpen(false);
              setFontFamilyAnchor(null);
            }}
            PaperProps={{
              sx: {
                mt: 0.5,
                borderRadius: '6px',
                border: `1px solid ${theme.textSecondary}20`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '160px',
                backgroundColor: theme.surface
              }
            }}
          >
            {fontFamilies.map((font) => (
              <MenuItem
                key={font.value}
                onClick={() => {
                  handleFontFamilyChange(font.value);
                  setFontFamilyMenuOpen(false);
                  setFontFamilyAnchor(null);
                }}
                sx={{
                  fontSize: '14px',
                  py: 1,
                  px: 2,
                  fontFamily: font.value,
                  '&:hover': {
                    backgroundColor: `${theme.textSecondary}08`
                  }
                }}
              >
                {font.name}
              </MenuItem>
            ))}
          </Menu>

          {/* Clean Font Size Selector */}
          <Button
            onClick={(e) => {
              setFontSizeAnchor(e.currentTarget);
              setFontSizeMenuOpen(true);
            }}
            sx={{
              minWidth: 'auto',
              height: '32px',
              padding: '6px 8px',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.text,
              fontSize: '14px',
              fontWeight: '400',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${theme.textSecondary}08`
              }
            }}
          >
            {fontSize}px
          </Button>

          {/* Font Size Menu */}
          <Menu
            anchorEl={fontSizeAnchor}
            open={fontSizeMenuOpen}
            onClose={() => {
              setFontSizeMenuOpen(false);
              setFontSizeAnchor(null);
            }}
            PaperProps={{
              sx: {
                mt: 0.5,
                borderRadius: '6px',
                border: `1px solid ${theme.textSecondary}20`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '80px',
                backgroundColor: theme.surface
              }
            }}
          >
            {fontSizes.map((size) => (
              <MenuItem
                key={size}
                onClick={() => {
                  handleFontSizeChange(size);
                  setFontSizeMenuOpen(false);
                  setFontSizeAnchor(null);
                }}
                sx={{
                  fontSize: '14px',
                  py: 1,
                  px: 2,
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: `${theme.textSecondary}08`
                  }
                }}
              >
                {size}px
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Subtle spacing */}
        <Box sx={{ width: '1px', height: '24px', backgroundColor: 'rgba(0,0,0,0.08)', mx: 1 }} />

        {/* Text Formatting with Staggered Animation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <ButtonGroup size="small" sx={{ '& .MuiButtonGroup-grouped': { border: 'none' } }}>
            <ToolbarButton
              command="bold"
              icon={<FormatBold fontSize="small" />}
              tooltip="Bold (Ctrl+B)"
              index={2}
            />
            <ToolbarButton
              command="italic"
              icon={<FormatItalic fontSize="small" />}
              tooltip="Italic (Ctrl+I)"
              index={3}
            />
            <ToolbarButton
              command="underline"
              icon={<FormatUnderlined fontSize="small" />}
              tooltip="Underline (Ctrl+U)"
              index={4}
            />
          </ButtonGroup>
        </motion.div>

        {/* Subtle spacing */}
        <Box sx={{ width: '1px', height: '24px', backgroundColor: 'rgba(0,0,0,0.08)', mx: 1 }} />

        {/* Link */}
        <Tooltip title="Insert Link" arrow placement="top">
          <IconButton
            size="small"
            onClick={(e) => setLinkAnchor(e.currentTarget)}
            sx={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              color: theme.textSecondary,
              transition: 'all 0.2s ease',
              '&:hover': { 
                backgroundColor: `${theme.primary}15`, 
                color: theme.primary,
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Link fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Image */}
        <Tooltip title="Insert Image">
          <IconButton
            size="small"
            onClick={(e) => setImageAnchor(e.currentTarget)}
            sx={{
              color: theme.textSecondary,
              '&:hover': { backgroundColor: `${theme.primary}20`, color: theme.primary }
            }}
          >
            <Image fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Table */}
        <Tooltip title="Insert Table (3x3)">
          <IconButton
            size="small"
            onClick={handleTableInsert}
            sx={{
              color: theme.textSecondary,
              '&:hover': { backgroundColor: `${theme.primary}20`, color: theme.primary }
            }}
          >
            <TableChart fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Inline Code */}
        <Tooltip title={isInInlineCodeState ? "Remove Inline Code (Ctrl+`)" : "Inline Code (Ctrl+`)"}>
          <IconButton
            size="small"
            onClick={handleInlineCode}
            sx={{
              color: isInInlineCodeState ? theme.primary : theme.textSecondary,
              backgroundColor: isInInlineCodeState ? `${theme.primary}15` : 'transparent',
              '&:hover': { backgroundColor: `${theme.primary}20`, color: theme.primary }
            }}
          >
            <Code fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Code Snippet */}
        <Tooltip title="Code Snippet">
          <IconButton
            size="small"
            onClick={(e) => setCodeSnippetAnchor(e.currentTarget)}
            sx={{
              color: theme.textSecondary,
              '&:hover': { backgroundColor: `${theme.primary}20`, color: theme.primary }
            }}
          >
            <ViewHeadline fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Format Code - Only show when in code block */}
        {isInCodeBlock && (
          <Tooltip title="Format Code">
            <IconButton
              size="small"
              onClick={handleFormatCurrentCodeBlock}
              sx={{
                color: theme.primary,
                backgroundColor: `${theme.primary}15`,
                '&:hover': { backgroundColor: `${theme.primary}25`, color: theme.primary }
              }}
            >
              <AutoFixHigh fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, backgroundColor: `${theme.textSecondary}30` }} />

        {/* Alignment - Keep only left align */}
        <ToolbarButton
          command="justifyLeft"
          icon={<FormatAlignLeft fontSize="small" />}
          tooltip="Align Left"
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, backgroundColor: `${theme.textSecondary}30` }} />

        {/* Lists */}
        <ButtonGroup size="small">
          <ToolbarButton
            command="insertUnorderedList"
            icon={<FormatListBulleted fontSize="small" />}
            tooltip="Bullet List"
          />
          <ToolbarButton
            command="insertOrderedList"
            icon={<FormatListNumbered fontSize="small" />}
            tooltip="Numbered List"
          />
          <Tooltip title="Checklist">
            <IconButton
              size="small"
              onClick={handleChecklistInsert}
              sx={{
                color: theme.textSecondary,
                '&:hover': {
                  backgroundColor: `${theme.primary}20`,
                  color: theme.primary
                },
                transition: 'all 0.2s ease'
              }}
            >
              <CheckBox fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        {/* Remove Indent buttons - Tab key works better */}
        {false && (
          <>
            <ButtonGroup size="small">
              <ToolbarButton
                command="outdent"
                icon={<FormatIndentDecrease fontSize="small" />}
                tooltip="Decrease Indent"
              />
              <ToolbarButton
                command="indent"
                icon={<FormatIndentIncrease fontSize="small" />}
                tooltip="Increase Indent"
              />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, backgroundColor: `${theme.textSecondary}30` }} />
          </>
        )}

        {/* Clear Formatting */}
        <Tooltip title="Clear Formatting">
          <IconButton
            size="small"
            onClick={() => executeCommand('removeFormat')}
            sx={{
              color: theme.textSecondary,
              '&:hover': { backgroundColor: `${theme.primary}20`, color: theme.primary }
            }}
          >
            <FormatClear fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Remove Analysis Buttons - Not needed during writing flow */}
        {false && showAnalysisButtons && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, backgroundColor: `${theme.textSecondary}30` }} />
            
            <Tooltip title="Analyze Text">
              <Button
                size="small"
                variant="contained"
                onClick={() => onAnalyze && onAnalyze('comprehensive')}
                disabled={isAnalyzing}
                sx={{
                  backgroundColor: theme.primary,
                  color: 'white',
                  minWidth: 'auto',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: theme.primary }
                }}
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze'}
              </Button>
            </Tooltip>
            
            <Tooltip title="Grammar Check">
              <Chip
                label="Grammar"
                size="small"
                onClick={() => onAnalyze && onAnalyze('grammar')}
                disabled={isAnalyzing}
                sx={{
                  backgroundColor: `${theme.success}15`,
                  color: theme.success,
                  border: `1px solid ${theme.success}30`,
                  '&:hover': { backgroundColor: `${theme.success}25` },
                  cursor: 'pointer',
                  ml: 0.5
                }}
              />
            </Tooltip>
            
            <Tooltip title="Style Improvement">
              <Chip
                label="Style"
                size="small"
                onClick={() => onAnalyze && onAnalyze('style')}
                disabled={isAnalyzing}
                sx={{
                  backgroundColor: `${theme.info}15`,
                  color: theme.info,
                  border: `1px solid ${theme.info}30`,
                  '&:hover': { backgroundColor: `${theme.info}25` },
                  cursor: 'pointer',
                  ml: 0.5
                }}
              />
            </Tooltip>
          </>
        )}
      </ToolbarContainer>

      {/* Table Context Menu - Now using MUI Menu instead of DOM manipulation */}
      <Menu
        anchorEl={tableMenuAnchor}
        open={Boolean(tableMenuAnchor)}
        onClose={() => setTableMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => selectedTableCell && handleInsertRowAbove(selectedTableCell.cell, selectedTableCell.table)}>
          Insert Row Above
        </MenuItem>
        <MenuItem onClick={() => selectedTableCell && handleInsertRowBelow(selectedTableCell.cell, selectedTableCell.table)}>
          Insert Row Below
        </MenuItem>
        <MenuItem onClick={() => selectedTableCell && handleInsertColumnLeft(selectedTableCell.cell, selectedTableCell.table)}>
          Insert Column Left
        </MenuItem>
        <MenuItem onClick={() => selectedTableCell && handleInsertColumnRight(selectedTableCell.cell, selectedTableCell.table)}>
          Insert Column Right
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => selectedTableCell && handleDeleteRow(selectedTableCell.cell, selectedTableCell.table)}
          sx={{ color: 'error.main' }}
        >
          Delete Row
        </MenuItem>
        <MenuItem 
          onClick={() => selectedTableCell && handleDeleteColumn(selectedTableCell.cell, selectedTableCell.table)}
          sx={{ color: 'error.main' }}
        >
          Delete Column
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => selectedTableCell && handleDeleteTable(selectedTableCell.table.id)}
          sx={{ color: 'error.main' }}
        >
          Delete Table
        </MenuItem>
      </Menu>

      {/* Link Insert Popover */}
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: theme.text }}>
            Insert Link
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter URL (e.g., https://example.com)"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLinkInsert()}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.background,
                '& fieldset': { borderColor: `${theme.textSecondary}30` },
                '&:hover fieldset': { borderColor: theme.primary },
                '&.Mui-focused fieldset': { borderColor: theme.primary }
              },
              '& .MuiInputBase-input': { color: theme.text }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => setLinkAnchor(null)}
              sx={{ color: theme.textSecondary }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleLinkInsert}
              disabled={!linkUrl.trim()}
              sx={{ backgroundColor: theme.primary }}
            >
              Insert
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Image Insert Popover */}
      <Popover
        open={Boolean(imageAnchor)}
        anchorEl={imageAnchor}
        onClose={() => {
          setImageAnchor(null);
          setImageFile(null);
          setImageUrl('');
          setUploadMethod('file');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: theme.text }}>
            Insert Image
          </Typography>
          
          {/* Upload Method Toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant={uploadMethod === 'file' ? 'contained' : 'outlined'}
              onClick={() => setUploadMethod('file')}
              sx={{ 
                backgroundColor: uploadMethod === 'file' ? theme.primary : 'transparent',
                borderColor: theme.primary,
                color: uploadMethod === 'file' ? 'white' : theme.primary
              }}
            >
              Upload File
            </Button>
            <Button
              size="small"
              variant={uploadMethod === 'url' ? 'contained' : 'outlined'}
              onClick={() => setUploadMethod('url')}
              sx={{ 
                backgroundColor: uploadMethod === 'url' ? theme.primary : 'transparent',
                borderColor: theme.primary,
                color: uploadMethod === 'url' ? 'white' : theme.primary
              }}
            >
              From URL
            </Button>
          </Box>

          {uploadMethod === 'file' ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                fullWidth
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                sx={{ 
                  mb: 2,
                  borderColor: theme.primary,
                  color: theme.primary,
                  '&:hover': { borderColor: theme.primary, backgroundColor: `${theme.primary}10` }
                }}
              >
                {imageFile ? `Selected: ${imageFile.name}` : 'Choose Image File'}
              </Button>
            </>
          ) : (
            <TextField
              fullWidth
              size="small"
              placeholder="Enter image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleImageInsert()}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.background,
                  '& fieldset': { borderColor: `${theme.textSecondary}30` },
                  '&:hover fieldset': { borderColor: theme.primary },
                  '&.Mui-focused fieldset': { borderColor: theme.primary }
                },
                '& .MuiInputBase-input': { color: theme.text }
              }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => {
                setImageAnchor(null);
                setImageFile(null);
                setImageUrl('');
                setUploadMethod('file');
              }}
              sx={{ color: theme.textSecondary }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleImageInsert}
              disabled={uploadMethod === 'file' ? !imageFile : !imageUrl.trim()}
              sx={{ backgroundColor: theme.primary }}
            >
              Insert
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Code Snippet Popover */}
      <Popover
        open={Boolean(codeSnippetAnchor)}
        anchorEl={codeSnippetAnchor}
        onClose={() => setCodeSnippetAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: theme.text }}>
            Insert Code Snippet
          </Typography>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: theme.textSecondary }}>
              Select Language:
            </Typography>
            
            {/* Clean Language Selector */}
            <Button
              onClick={(e) => {
                setLanguageAnchor(e.currentTarget);
                setLanguageMenuOpen(true);
              }}
              sx={{
                width: '100%',
                height: '40px',
                padding: '8px 12px',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                border: `1px solid ${theme.textSecondary}30`,
                color: theme.text,
                fontSize: '14px',
                fontWeight: '400',
                textTransform: 'none',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: `${theme.textSecondary}05`,
                  borderColor: `${theme.textSecondary}50`
                }
              }}
            >
              {programmingLanguages.find(lang => lang.value === selectedLanguage)?.name || 'Select Language'}
            </Button>

            {/* Language Menu */}
            <Menu
              anchorEl={languageAnchor}
              open={languageMenuOpen}
              onClose={() => {
                setLanguageMenuOpen(false);
                setLanguageAnchor(null);
              }}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  borderRadius: '6px',
                  border: `1px solid ${theme.textSecondary}20`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minWidth: '200px',
                  backgroundColor: theme.surface,
                  maxHeight: '300px'
                }
              }}
            >
              {programmingLanguages.map((lang) => (
                <MenuItem
                  key={lang.value}
                  onClick={() => {
                    setSelectedLanguage(lang.value);
                    setLanguageMenuOpen(false);
                    setLanguageAnchor(null);
                  }}
                  sx={{
                    fontSize: '14px',
                    py: 1,
                    px: 2,
                    '&:hover': {
                      backgroundColor: `${theme.textSecondary}08`
                    }
                  }}
                >
                  {lang.name}
                </MenuItem>
              ))}
            </Menu>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => setCodeSnippetAnchor(null)}
              sx={{ color: theme.textSecondary }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleCodeSnippetInsert}
              sx={{ backgroundColor: theme.primary }}
            >
              Insert
            </Button>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

// Wrap with React.memo for performance optimization
const MemoizedRichTextToolbar = React.memo(RichTextToolbar);
MemoizedRichTextToolbar.displayName = 'RichTextToolbar';

export default MemoizedRichTextToolbar;
